import { Link, Stack } from "@mui/material"
import { Avatar, Button, Chip, CopyToClipboard, DataTable, DialogHandler, Dropdown, EmptyContent, EnhancedFormModel, fetchRoute, FormModel, FormsFactory, FullBar, GenericWrapperContext, getCalendarFormat, Header, Icon, LoadingContainer, Search, SummaryEnhancedFormModel, TCopyToClipboard, TDataTableColumnsData, TDataTableRowData, TGenericListReturn, TOrder, TPromisePoolPartialList, TPromisePoolRef, TPromisePoolReturnData, Typography, UserModel, UsersFactory } from "@cakemail-org/ui-components-v2"
import { TFormsListing } from "../types"
import { useContext, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { NameTableCol } from "../../../components/tables/nameTableCol"
import { listsPromissePool } from "../../../utils/promisesPools"
import { ERoutes } from "../../../utils/url"
import { showDeleteDialog } from "../../../components/dialogs/deleteDialog"
import { buildFromScript, buildLink } from "../utils"
import { showRenameDialog } from "../../../components/dialogs/rename"

export default function FormsListing({ }: TFormsListing) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { t } = useTranslation()
    const copyToClipboardRef = useRef<TCopyToClipboard>(null)

    const [openedSearch, setOpenedSearch] = useState(false)
    const [search, setSearch] = useState("")
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(25)
    const [order, setOrder] = useState<TOrder>("asc")
    const [orderBy, setOrderBy] = useState<string>("-created_on")
    const [forms, setForms] = useState<TGenericListReturn<SummaryEnhancedFormModel | FormModel>>()
    const [loading, setLoading] = useState(false)

    const [users, setUsers] = useState<{ [id: string]: UserModel }>()

    const [partialLists, setPartialLists] = useState<TPromisePoolReturnData<TPromisePoolPartialList>>({})
    const partialListsPromiseRef = useRef<TPromisePoolRef>(undefined)

    useEffect(() => {
        UsersFactory.list({
            page: 1,
            perPage: 1000
        }).then((res) => {
            const curUsers: any = {}
            res.data.forEach((user: UserModel) => {
                curUsers[user.id] = user
            })
            setUsers(curUsers)
        })
    }, [])

    useEffect(handleSearchFocus, [openedSearch])

    useEffect(() => {
        fetchForms()
    }, [page, perPage, order, orderBy, search])

    useEffect(() => {
        return () => {
            stopPromisePools()
        }
    }, [])

    function stopPromisePools() {
        try {
            partialListsPromiseRef.current?.stop()
            partialListsPromiseRef.current = undefined
        } catch (e) { }
    }

    function fetchForms() {
        setLoading(true)
        FormsFactory.list({
            page,
            perPage,
            sort: order === "asc" ? orderBy : `-${orderBy}`,
            filter: search ? `name==${search}` : undefined,
            withCount: true
        }).then((res) => {

            const listIds = res.data.map((form: SummaryEnhancedFormModel | FormModel) => { return { id: form.list_id } })
            listsPromissePool({ data: listIds, setter: setPartialLists, poolRef: partialListsPromiseRef })

            setForms(res)
            setLoading(false)
        }).catch((err) => {
            setLoading(false)
        })
    }

    function handleCreateForm() {
        window.location.href = "#" + fetchRoute({ route: ERoutes.formDetails, params: { formId: "new" } })
    }

    function handleSearchFocus() {
        if (!openedSearch) return
        const el = document.querySelector(".formsSearch input") as HTMLInputElement
        if (el) el.focus()
    }

    function handleSearchChange(cSearch: string) {
        setPage(1)
        setSearch(cSearch)
    }

    function handleSortChange(direction: TOrder, property: keyof TDataTableRowData) {
        setOrder(direction)
        setOrderBy(property as string)
    }

    function handlePaginationChange(cPage: number, cPerPage: number) {
        setPage(cPage)
    }

    function handleSearchOpen() {
        setSearch("")
        setOpenedSearch(!openedSearch)
    }

    function handleOpenForm(form: SummaryEnhancedFormModel | FormModel) {
        if (form instanceof SummaryEnhancedFormModel) {
            window.location.href = "#" + fetchRoute({ route: ERoutes.formDetails, params: { formId: form.id } })
        } else {
            window.location.href = "#" + fetchRoute({ route: ERoutes.formEditBasic, params: { formId: form.id } })
        }
    }

    function handleDuplicateForm(form: SummaryEnhancedFormModel | FormModel) {
        FormsFactory.get({ id: form.id })
            .then((data) => {
                data.name = data.name + ` (${t("general-copy")})`
                return FormsFactory.create({ form: data.toJson(), type: data instanceof EnhancedFormModel ? "enhanced" : "basic" })
            })
            .then(() => {
                fetchForms()
            }).catch((e) => {
                DialogHandler.show({
                    isExpress: true,
                    type: "error",
                    title: t("general-error-action"),
                })
            })
    }

    function handleRenameForm(form: SummaryEnhancedFormModel | FormModel) {
        showRenameDialog({ initName: form.name, resourceKey: "general-form_one" })
            .then(({ value }) => {
                form.name = value
                return form.save()
            }).then(() => {
                fetchForms()
                DialogHandler.hide()
            }).catch((e) => {
                if (e) {
                    DialogHandler.show({
                        isExpress: true,
                        type: "error",
                        title: t("general-error-action"),
                    })
                }
            })
    }

    function handlePreviewForm(form: SummaryEnhancedFormModel | FormModel) {
        if (form instanceof SummaryEnhancedFormModel) {
            window.location.href = "#" + fetchRoute({ route: ERoutes.formDetails, params: { formId: form.id } }) + "?preview=true"
        } else {
            window.location.href = "#" + fetchRoute({ route: ERoutes.formEditBasic, params: { formId: form.id } })
        }

    }

    function handleCopyFormInfo(form: SummaryEnhancedFormModel | FormModel, type: "link" | "snippet") {
        if (form instanceof SummaryEnhancedFormModel) {
            if (type === "link") {
                copyToClipboardRef.current?.copy(buildLink({ form, account: genericWrapper.account, user: genericWrapper.user }))
            } else {
                copyToClipboardRef.current?.copy(buildFromScript({ form }))
            }
        } else {
            if (type === "link") {
                copyToClipboardRef.current?.copy(form.url.linked)
            } else {
                copyToClipboardRef.current?.copy(form.content.embedded)
            }

        }

    }

    function handleDeleteForm(form: SummaryEnhancedFormModel | FormModel) {
        showDeleteDialog({
            name: form.name,
            resourceKey: "general-form_one",
            validationWordKey: "input-validation-key-delete"
        }).then(({ event, btnRef }) => {
            btnRef.animate()
            return form.delete()
        }).then(() => {
            DialogHandler.show({
                isExpress: true,
                type: "success",
                title: t("form-deleted")
            })
            fetchForms()
        }).catch((e) => {
            if (e)
                DialogHandler.show({
                    isExpress: true,
                    type: "warning",
                    title: t("general-error-action")
                })
        })
    }


    const colsData: TDataTableColumnsData[] = [
        {
            id: "name",
            label: t("general-form_other"),
            sortable: true,
        },
        /*{
            id: "conversion",
            label: t("general-conversion"),
            sortable: true,
        },
        {
            id: "views",
            label: t("general-view_other"),
            sortable: true,
        },*/
        {
            id: "published_on",
            label: t("general-status"),
            sortable: true,
        },
        {
            id: "branding",
            label: t("general-branding"),
            sortable: true,
        },
        {
            id: "edited_by",
            label: t("general-edited_by"),
        },
        {
            id: "created_on",
            label: t("general-created_on"),
            sortable: true,
        },
        {
            id: "updated_on",
            label: t("general-updated_on"),
            sortable: true,
        },
        {
            id: "list_id",
            label: t("general-list_one"),
            sortable: true,
        },
        {
            id: "actions",
            label: ""
        }
    ]

    //@ts-ignore
    const rows: any[] = forms && forms.data.map((form: SummaryEnhancedFormModel | FormModel, index) => {
        const isEnhanced = "enabled" in form
        const partialList: Partial<TPromisePoolPartialList> = form.list_id ? partialLists?.[form.list_id] : {}

        const actions = <Stack direction="row" gap={3}>
            <Button
                onClick={() => { handleOpenForm(form) }}
                size="small"
                color="body1"
                variant="outlined"
            >{t("general-open_one")}</Button>
            <Dropdown
                size="medium"
                color="body1"
                variant="outlined"
                iconOnly
                options={[
                    {
                        isAction: true,
                        text: t("general-preview"),
                        postContent: <Icon name="Preview18" />,
                        onClick: () => { handlePreviewForm(form) }
                    },
                    {
                        isAction: true,
                        text: t("forms-copy-link"),
                        postContent: <Icon name="Copy18" />,
                        onClick: () => { handleCopyFormInfo(form, "link") }
                    },
                    {
                        isAction: true,
                        text: t("forms-copy-snippet"),
                        postContent: <Icon name="Copy18" />,
                        onClick: () => { handleCopyFormInfo(form, "snippet") }
                    },
                    {
                        isAction: true,
                        text: t("general-rename"),
                        postContent: <Icon name="Edit18" />,
                        onClick: () => { handleRenameForm(form) }
                    },
                    {
                        isAction: true,
                        text: t("general-duplicate"),
                        postContent: <Icon name="Document18" />,
                        onClick: () => { handleDuplicateForm(form) }
                    },
                    {
                        isAction: true,
                        text: t("general-delete"),
                        postContent: <Icon name="Delete18" />,
                        onClick: () => { handleDeleteForm(form) }
                    }
                ]}
            />
        </Stack>

        const commonInfo = {
            created_on: <Typography variant="body1L">{form.created_on !== 0 ? getCalendarFormat({ unixDate: form.created_on, showTime: true }) : ""}</Typography>,
            list_id: <Link href={"#" + fetchRoute({ route: ERoutes.lists }) + "?listId=" + form.list_id}>{partialList?.name || form.list_id}</Link>
        }

        if (isEnhanced) {
            const editedBy = users && users[form.edited_by.id]
            const isSmartBranding = form.branding === "smart"
            return {
                name: {
                    id: form.name,
                    content: <NameTableCol name={form.name} desc={form.description} imgSrc={form.thumbnail_url} />
                },
                edited_by: editedBy ? <Stack direction="row" gap={3} alignItems="center">
                    <Avatar variant="square" email={editedBy.email} firstName={editedBy.first_name} lastName={editedBy.last_name} />
                    <Typography variant="body1L">{editedBy.first_name} {editedBy.last_name}</Typography>
                </Stack> : "-",
                published_on: <Chip label={form.published_on > 0 ? t("general-published") : t("general-unpublished")} color={form.published_on > 0 ? "secondary" : "primary"} />,
                branding: <Chip label={t(isSmartBranding ? "general-smart" : "general-basic")} color={isSmartBranding ? "shade150" : "shade150"} />,
                updated_on: <Typography variant="body1L">{form.updated_on !== 0 ? getCalendarFormat({ unixDate: form.updated_on, showTime: true }) : ""}</Typography>,
                actions,
                ...commonInfo
            }
        }

        return {
            name: {
                id: form.name,
                content: <NameTableCol name={form.name} imgSrc={form.thumbnail_url} desc={`${t("general-basic")} ${t("general-form_one")}`} />
            },
            published_on: <Chip label={t("general-published")} color={"secondary"} />,
            branding: "",
            edited_by: "",
            updated_on: "",
            actions,
            ...commonInfo
        }
    }) || []

    return <Stack className="formsListingContainer" gap={8}>
        <CopyToClipboard ref={copyToClipboardRef} />
        <FullBar
            className={loading ? "loading" : ""}
            left={<Header
                heading={t("general-form_other")}
                headingVariant="titleL"
                support={genericWrapper.account?.name}
                headingTooltip={t("form-heading-tooltip")}
            />}
            right={[
                <Button
                    key="create-form"
                    variant="contained"
                    size="medium"
                    color="secondary"
                    onClick={handleCreateForm}
                    iconOnly
                    disabled={loading}
                ><Icon name="Add18" /></Button>,
                <Button
                    variant="outlined"
                    size="medium"
                    color="body1"
                    onClick={handleSearchOpen}
                    iconOnly
                    disabled={loading}
                ><Icon name="Search18" /></Button>
            ]}
        />

        {openedSearch &&
            <Search placeholder={t("assets-templates-listing-search-placeholder")} initValue={search} className={`formsSearch ${loading ? "loading" : ""}`} onChange={handleSearchChange} onClose={handleSearchOpen} useClickAway />
        }

        <LoadingContainer loading={!forms}>
            {forms && forms.data.length === 0 && !search &&
                <EmptyContent
                    title={t("forms-listing-empty-title")}
                    desc={t("forms-listing-empty-desc")}
                    iconName="CreateForm44"
                    button={<Button
                        variant="contained"
                        size="medium"
                        color="secondary"
                        onClick={handleCreateForm} iconOnly>
                        {t("general-create-form")}
                    </Button>}
                />
            }

            {forms && forms.data.length > 0 &&
                <DataTable
                    initState={{
                        rowsPerPage: perPage,
                        order,
                        orderBy
                    }}
                    totalCount={forms?.pagination?.count}
                    className={`formsDataTable ${loading ? "loading" : ""}`}
                    onPaginationChange={handlePaginationChange}
                    onSortChange={handleSortChange}
                    rows={rows}
                    columnsData={colsData}
                    stickyLastCol
                />
            }

        </LoadingContainer>
    </Stack>
}